import {
  REFERENCE_SPAN_PATTERN,
  REFERENCE_SPAN_ID_PATTERN,
  REFERENCE_ID_SPAN_PATTERN,
  REFERENCE_COMPLEX_PATTERN,
} from '../regex';

/**
 * Converts Reference node HTML back to reference text format ({{inputs.ID}} or {{steps.ID}})
 * This is used when saving content to ensure references are stored in the right format
 *
 * @param html The HTML content from the editor
 * @returns Transformed HTML with references in text format
 */
export const convertReferenceNodesToText = (html: string): string => {
  // First, try direct regex replacement for better performance
  let processedHtml = html;
  let replacementsMade = false;

  // 1. Handle span format with data-reference and data-id for complex expressions
  processedHtml = processedHtml.replace(REFERENCE_SPAN_PATTERN, (match, id) => {
    replacementsMade = true;
    return `{{${id}}}`;
  });

  // 2. Handle span format with data-reference="" id="complex.expression"
  if (!replacementsMade) {
    processedHtml = processedHtml.replace(
      REFERENCE_SPAN_ID_PATTERN,
      (match, id) => {
        replacementsMade = true;
        return `{{${id}}}`;
      }
    );
  }

  // 3. Handle span format with id="complex.expression" data-reference=""
  if (!replacementsMade) {
    processedHtml = processedHtml.replace(
      REFERENCE_ID_SPAN_PATTERN,
      (match, id) => {
        replacementsMade = true;
        return `{{${id}}}`;
      }
    );
  }

  // 4. Handle span format with id and data-reference together with other attributes
  if (!replacementsMade) {
    processedHtml = processedHtml.replace(
      REFERENCE_COMPLEX_PATTERN,
      (match, id) => {
        replacementsMade = true;
        return `{{${id}}}`;
      }
    );
  }

  // If no references were found with the direct approach, use the DOM parsing approach as a fallback
  if (!replacementsMade && html.includes('data-reference')) {
    // Create a temporary div to parse the HTML
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = html;

    // Find all reference nodes
    const referenceNodes = tempDiv.querySelectorAll('span[data-reference]');

    // Create a map to verify correct formatting of reference IDs
    const referencesFound = new Map();
    let domReplacementsMade = false;

    // Replace each reference node with the reference text format
    referenceNodes.forEach((node) => {
      // Try to get the ID from different possible attribute formats
      const referenceId =
        node.getAttribute('id') ||
        node.getAttribute('data-id') ||
        node.getAttribute('data-reference-id');

      if (referenceId) {
        // Make sure the referenceId is properly formatted (without curly braces)
        let formattedId = referenceId;

        // Remove curly braces if present to avoid double braces
        if (formattedId.startsWith('{{') && formattedId.endsWith('}}')) {
          formattedId = formattedId.substring(2, formattedId.length - 2);
        }

        // Form the proper template format
        const referenceTemplate = `{{${formattedId}}}`;

        // Create a text node with the template format
        const textNode = document.createTextNode(referenceTemplate);
        node.parentNode?.replaceChild(textNode, node);
        domReplacementsMade = true;

        // Store for verification
        referencesFound.set(formattedId, referenceTemplate);
      } else {
        console.warn(
          'convertReferenceNodesToText: Found reference node without ID attributes',
          node.outerHTML
        );
      }
    });

    // Log all references that were processed
    if (domReplacementsMade) {
      // Get the transformed HTML
      processedHtml = tempDiv.innerHTML;
    } else {
      console.warn(
        'convertReferenceNodesToText: No references were converted using DOM approach either'
      );
    }
  }

  // Return the transformed HTML
  return processedHtml;
};
